import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, WheelEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { AffectedNode, Node } from '@/shared/api/generated/lineage';

import { KIND_COLOUR, NODE_HEIGHT, NODE_WIDTH, layOut, relatedTo } from '../model/graph';

/* Lineage as a flowing graph: upstream on the left, the subject in the middle, downstream on the right.
 *
 * **Pan and zoom are one `transform` on one `<g>`**, which is why this needs no library and stays smooth:
 * the browser composites a single transformed group rather than re-laying-out anything. Fit-to-screen is
 * arithmetic against the layout's own width and height.
 *
 * **Selecting a node dims everything unrelated instead of hiding it.** Hiding would make the graph
 * change shape under the reader's hands and lose the context they selected *within*; dimming keeps the
 * whole picture and answers "what does this feed" in place. Upstream and downstream get different
 * treatments, because "what am I made of" and "what breaks if I break" are the two different questions
 * a lineage graph is opened for.
 *
 * **Keyboard-reachable, which an SVG graph usually is not.** Every node is a `<g tabindex="0">` with a
 * role and an accessible name, arrow keys move along the layers, and Enter opens the detail. A graph
 * only usable with a mouse is a graph an auditor with a screen reader cannot audit — and the node list
 * is also rendered as an ordinary list below for exactly that reason, so the information is never
 * *only* in the picture.
 */

export interface LineageGraphProps {
  readonly subject: Node;
  readonly provenance: readonly AffectedNode[];
  readonly impact: readonly AffectedNode[];
  readonly selectedId: string | null;
  readonly onSelect: (nodeId: string | null) => void;
}

/** What sits on the pan surface and must not start a drag. See `onPointerDown`.
 *
 * One selector rather than two checks, so a third overlay added to the frame is one entry away from
 * being correct instead of one forgotten condition away from being silently unclickable. */
const NON_PANNING = '.lq-graph__node, .lq-graph__zoom';

const MIN_SCALE = 0.4;
const MAX_SCALE = 2.4;

export function LineageGraph({
  subject,
  provenance,
  impact,
  selectedId,
  onSelect,
}: LineageGraphProps): ReactElement {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number } | null>(null);

  const layout = useMemo(
    () => layOut(subject, provenance, impact),
    [subject, provenance, impact],
  );

  const related = useMemo(
    () => (selectedId === null ? null : relatedTo(layout, selectedId)),
    [layout, selectedId],
  );

  const fit = useCallback(() => {
    const box = frame.current?.getBoundingClientRect();
    if (box === undefined) return;
    /* Never scales *up* past 1 to fill the frame: a three-node graph blown up to fill a widescreen
     * monitor looks like a diagram of nothing, and the reader loses the sense that it is small. */
    const next = Math.min(1, box.width / layout.width, box.height / layout.height);
    setScale(Math.max(MIN_SCALE, next));
    setPan({ x: 0, y: 0 });
  }, [layout.width, layout.height]);

  /* Fit whenever the graph changes identity, so asking about a new node does not leave the viewport
     parked where the previous graph happened to be. */
  useEffect(() => {
    fit();
  }, [fit]);

  const onWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    /* Only with a modifier. A bare wheel over a tall page must keep scrolling the page — a graph that
       swallows the scroll is the single most complained-about behaviour in embedded diagrams. */
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setScale((current) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, current * (event.deltaY < 0 ? 1.1 : 0.9))),
    );
  }, []);

  return (
    <div className="lq-graph">
      {/* **The zoom controls are on the canvas, not above it, and that is the third position they
          have had.** Above it they were a control strip for an interaction most readers never use,
          and the reference has a bare canvas. Gone entirely they left a real gap: `fit` never scales
          *up* past 1, so a wide graph — a fan-out of five dims is 1,600px — opens shrunk to fit and
          there is no way to make it readable again. A modifier-plus-wheel is not an answer to that:
          it is undiscoverable, and on a trackpad it is a gesture people do not know they have.

          Overlaid in the corner is the convention in every map and diagram tool, and it keeps both
          properties: the picture is unframed by furniture, and the one interaction a reader
          predictably needs is one click away. The double-click refit stays as the recovery from a
          lost viewport, which is the case a button is *worse* at — by then the buttons may be the
          only thing on screen and pressing Fit is not the obvious move. */}
      <div
        className="lq-graph__frame"
        ref={frame}
        onWheel={onWheel}
        /* The recovery. `detail === 2` rather than `onDoubleClick`, because a double-click that
           landed on a node would otherwise both refit *and* toggle that node's selection — the
           second click's `click` still reaches the node. Refitting is the whole intent of the
           gesture, so the selection is left alone. */
        onDoubleClick={(event) => {
          event.preventDefault();
          fit();
        }}
        onPointerDown={(event) => {
          /* **Not when the press landed on something that sits on the pan surface.** The frame *is*
             the pan surface and both the nodes and the zoom cluster are on top of it, so without
             this every press on one of them also begins a drag.

             For a node that nudged the whole graph. For the zoom controls it was worse and is why
             they appeared not to work at all: `setPointerCapture` below redirects every later
             pointer event to the frame, so the button never gets its `pointerup` and therefore never
             gets a `click`. Pressing + or − did nothing at all — reported from the real browser, and
             **the jsdom test passed** because jsdom has no `setPointerCapture`, so the line that
             causes it threw before the capture could happen. A test that passes because the
             environment lacks the mechanism that breaks it is not evidence.

             `closest` rather than `stopPropagation` on the children, because they need their own
             clicks and swallowing the event there would break selection and zoom alike. */
          if ((event.target as Element).closest(NON_PANNING) !== null) return;
          dragging.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const from = dragging.current;
          if (from === null) return;
          /* Guarded on the button still being held. A pointerup delivered outside the element — or
             swallowed entirely, which happens when a drag ends over a different window — would
             otherwise leave `dragging` set and make the graph follow the cursor with no button down. */
          if (event.buttons === 0) {
            dragging.current = null;
            return;
          }
          setPan({ x: event.clientX - from.x, y: event.clientY - from.y });
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
        onPointerCancel={() => {
          dragging.current = null;
        }}
        onPointerLeave={() => {
          dragging.current = null;
        }}
      >
        <svg
          className="lq-graph__svg"
          role="img"
          aria-label={t('lineage.graph.label', { name: subject.display_name })}
        >
          <g
            transform={`translate(${String(pan.x)} ${String(pan.y)}) scale(${String(scale)})`}
          >
            {layout.edges.map((edge) => {
              const from = layout.nodes.find((node) => node.id === edge.from);
              const to = layout.nodes.find((node) => node.id === edge.to);
              if (from === undefined || to === undefined) return null;
              const x1 = from.x + NODE_WIDTH;
              const y1 = from.y + NODE_HEIGHT / 2;
              const x2 = to.x;
              const y2 = to.y + NODE_HEIGHT / 2;
              const mid = (x1 + x2) / 2;
              /* An edge is dimmed when either end is a sibling of the selection — the only end that
                 is provably unrelated. See `relatedTo`: dimming "not upstream and not downstream"
                 dimmed nothing, because in a layered graph every node is one or the other. */
              const dim =
                related !== null &&
                (related.siblings.has(edge.from) || related.siblings.has(edge.to));
              return (
                <path
                  key={`${edge.from}-${edge.to}`}
                  /* A cubic with horizontal control points, so every connector leaves a node
                     horizontally and arrives horizontally. Straight diagonals across a layered graph
                     make two crossing edges impossible to follow. */
                  d={`M ${String(x1)} ${String(y1)} C ${String(mid)} ${String(y1)}, ${String(mid)} ${String(y2)}, ${String(x2)} ${String(y2)}`}
                  className={
                    dim ? 'lq-graph__edge lq-graph__edge--dim' : 'lq-graph__edge'
                  }
                  markerEnd="url(#lq-arrow)"
                />
              );
            })}

            {layout.nodes.map((node) => {
              const isSelected = node.id === selectedId;
              const dim = related?.siblings.has(node.id) === true;
              return (
                <g
                  key={node.id}
                  className={
                    dim ? 'lq-graph__node lq-graph__node--dim' : 'lq-graph__node'
                  }
                  transform={`translate(${String(node.x)} ${String(node.y)})`}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                  /* The connection is in the accessible name, not only in the second line of
                     text: a screen reader user asking "which system is this?" must get the same
                     answer a sighted reader gets from the box.

                     **Two keys rather than one message with a conditional clause**, which is this
                     catalogue's own rule — see `completenessKey`: "one key per state, so no
                     template branches". ICU cannot select on an empty string, so the alternative
                     would have been a message that renders "in ." for a job. */
                  aria-label={
                    node.connection === null
                      ? t('lineage.graph.nodeLabel', {
                          name: node.label,
                          kind: t(`lineage.kind.${node.kind}`),
                          depth: node.depth,
                        })
                      : t('lineage.graph.nodeLabelIn', {
                          name: node.label,
                          kind: t(`lineage.kind.${node.kind}`),
                          depth: node.depth,
                          system: `${node.connection.platform} ${node.connection.name}`,
                        })
                  }
                  onClick={() => {
                    onSelect(isSelected ? null : node.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onSelect(isSelected ? null : node.id);
                  }}
                >
                  <rect
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={8}
                    className={
                      isSelected
                        ? 'lq-graph__box lq-graph__box--selected'
                        : node.subject
                          ? 'lq-graph__box lq-graph__box--subject'
                          : 'lq-graph__box'
                    }
                  />
                  {/* **A dot rather than a leading bar, and the kind's name moved to the legend.**
                      Three kinds cannot carry three memorable glyphs, so the mark stays a colour —
                      but a second text line per node spent a third of the box restating something
                      the legend says once, and it is what forced a 52px box. The kind is still in
                      the node's accessible name, so nothing is *only* in the colour. */}
                  <circle
                    cx={16}
                    cy={NODE_HEIGHT / 2}
                    r={4}
                    fill={KIND_COLOUR[node.kind]}
                  />
                  {/* **Two lines, and the second is the connection.** A box labelled only
                      `ANALYTICS.CUSTOMER` is ambiguous the moment a second connection holds a table
                      of that name — a prod and a dev Snowflake, or a Snowflake and a Databricks
                      table — which is exactly what a unified lineage graph is for. See
                      `NODE_HEIGHT` for why the taller box is a reversal on purpose.

                      Truncated at 18 and 22 characters respectively rather than wrapped: a wrapped
                      name changes the box's height, and the layout arithmetic upstream assumes
                      every box is the same size. The full name is in the accessible name and in the
                      detail beside the graph. */}
                  <text x={30} y={20} className="lq-graph__name">
                    {node.label.length > 18 ? `${node.label.slice(0, 17)}…` : node.label}
                  </text>
                  {node.connection === null ? (
                    /* **Two different absences, and conflating them was misleading.** Both used to
                       read "no connection", which on a job says we failed to resolve something —
                       when the truth is that a connection is a property of a *catalogued dataset*
                       (`Node` refuses one without a `dataset_id`) and jobs have no place for it.
                       For the `load from fact_sales.csv` step of a Snowflake harvest that reads as
                       a defect: the statement plainly ran inside that connection.

                       Deriving a job's connection from the dataset it writes was the tempting fix
                       and is refused — an Airflow job writing into Snowflake ran in Airflow, so the
                       badge would name a system the step is not in. Recording it properly needs a
                       column on `lineage.node`; until then each absence says what it actually is.

                       An empty second line still reads as a rendering fault, so neither is blank. */
                    <text
                      x={30}
                      y={36}
                      className="lq-graph__system lq-graph__system--none"
                    >
                      {t(
                        node.kind === 'job'
                          ? 'lineage.graph.jobStep'
                          : 'lineage.graph.notHarvested',
                      )}
                    </text>
                  ) : (
                    <text x={30} y={36} className="lq-graph__system">
                      {`${node.connection.platform} · ${node.connection.name}`.length > 22
                        ? `${`${node.connection.platform} · ${node.connection.name}`.slice(0, 21)}…`
                        : `${node.connection.platform} · ${node.connection.name}`}
                    </text>
                  )}
                </g>
              );
            })}
          </g>

          <defs>
            <marker
              id="lq-arrow"
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 1 L 7 4 L 0 7 z" fill="currentColor" />
            </marker>
          </defs>
        </svg>

        {/* Inside the frame so they float over the canvas, and **after** the `<svg>` so they paint
            above it without needing a `z-index`. `aria-label` on each rather than trusting the glyph:
            "+" and "−" are not names, and a screen reader announcing "plus button" says nothing about
            what it does. */}
        <div className="lq-graph__zoom">
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.zoomIn')}
            title={t('lineage.graph.zoomIn')}
            onClick={() => {
              setScale((current) => Math.min(MAX_SCALE, current * 1.2));
            }}
          >
            +
          </button>
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.zoomOut')}
            title={t('lineage.graph.zoomOut')}
            onClick={() => {
              setScale((current) => Math.max(MIN_SCALE, current / 1.2));
            }}
          >
            −
          </button>
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.fit')}
            title={t('lineage.graph.fit')}
            onClick={fit}
          >
            {/* A frame glyph rather than the word, so the cluster stays three equal squares. The
                accessible name and the tooltip both say "Fit to screen". */}
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
